import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import {
  PencilFill, CheckLg, XLg, PlusCircle, Trash, ArrowRight,
  Gear, Cpu, Lightning, ShieldLock, Lightbulb, GraphUp,
} from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/SecurityContext';
import RoleGuard from '../security/RoleGuard';
import SectionControls from '../SectionControls';
import { updateCardGridSection } from '../../services/contentService';
import ImageUrlField from './ImageUrlField';
import './CardGridBlock.scss';

function normalizePartners(partners) {
  if (!partners) return [];
  if (Array.isArray(partners)) {
    return partners.map((p) => (
      typeof p === 'string'
        ? { name: p, logoUrl: '' }
        : { name: p?.name || '', logoUrl: p?.logoUrl || '' }
    ));
  }
  if (typeof partners === 'string') {
    return partners
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((name) => ({ name, logoUrl: '' }));
  }
  return [];
}

// Small fixed icon set the admin can pick from per card. Keeping the set
// small keeps the editor UI simple — extend later if needed.
const ICON_OPTIONS = [
  { value: 'gear', label: 'Gear', Component: Gear },
  { value: 'cpu', label: 'Chip', Component: Cpu },
  { value: 'lightning', label: 'Lightning', Component: Lightning },
  { value: 'shield', label: 'Shield', Component: ShieldLock },
  { value: 'lightbulb', label: 'Lightbulb', Component: Lightbulb },
  { value: 'graph', label: 'Graph', Component: GraphUp },
];

const ICON_BY_VALUE = Object.fromEntries(ICON_OPTIONS.map((i) => [i.value, i.Component]));

const STATUS_COLORS = [
  { value: 'blue', label: 'Blue' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'green', label: 'Green' },
  { value: 'darkgreen', label: 'Dark green' },
];

function defaultCard() {
  return {
    title: 'Card title',
    description: 'Describe the challenge in one or two short sentences.',
    status: 'Open for submissions',
    statusColor: 'yellow',
    icon: 'gear',
    linkUrl: '',
    partners: [],
  };
}

export function defaultCardGridData() {
  return {
    title: 'Active challenges',
    cards: [defaultCard(), defaultCard()],
  };
}

// Read-only renderer. Used by both the public site (via ContentRenderer) and
// the admin view-mode of this component.
export function CardGridView({ data }) {
  const cards = Array.isArray(data?.cards) ? data.cards : [];
  return (
    <div className="card-grid-block">
      {data?.title ? (
        <h2 className="card-grid-block__title">{data.title}</h2>
      ) : null}
      <Row className="card-grid-block__cards g-4 justify-content-center">
        {cards.map((card, idx) => {
          const Icon = ICON_BY_VALUE[card.icon] || Gear;
          const partners = normalizePartners(card.partners).filter((p) => p.name || p.logoUrl);
          const inner = (
            <div className="card-grid-block__card">
              <div className="card-grid-block__card-header">
                <Icon className="card-grid-block__card-icon" />
              </div>
              <div className="card-grid-block__card-body">
                {card.status ? (
                  <span className={`card-grid-block__status card-grid-block__status--${card.statusColor || 'blue'}`}>
                    {card.status}
                  </span>
                ) : null}
                <h3 className="card-grid-block__card-title">{card.title}</h3>
                <p className="card-grid-block__card-description">{card.description}</p>
                {partners.length > 0 ? (
                  <div className="card-grid-block__card-partners">
                    <span className="card-grid-block__partners-label">Partners:</span>
                    <div className="card-grid-block__partners-list">
                      {partners.map((p, pIdx) => (
                        <div key={pIdx} className="card-grid-block__partner">
                          {p.logoUrl ? (
                            <img src={p.logoUrl} alt={p.name || ''} className="card-grid-block__partner-logo" />
                          ) : (
                            <span className="card-grid-block__partner-name">{p.name}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                {card.linkUrl ? (
                  <span className="card-grid-block__card-cta">
                    Learn more &amp; apply <ArrowRight />
                  </span>
                ) : null}
              </div>
            </div>
          );
          return (
            <Col md={6} key={idx} className="card-grid-block__card-col">
              {card.linkUrl ? (
                <a href={card.linkUrl} className="card-grid-block__card-link">
                  {inner}
                </a>
              ) : (
                inner
              )}
            </Col>
          );
        })}
      </Row>
    </div>
  );
}

// Admin wrapper — view + edit + section controls.
function CardGridBlock({
  pageId,
  section,
  onMoveUp,
  onMoveDown,
  onDelete,
  isFirstSection,
  isLastSection,
}) {
  const { user } = useAuth();
  const initialData = useMemo(() => section?.data || defaultCardGridData(), [section]);
  const [data, setData] = useState(initialData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setData(section?.data || defaultCardGridData());
  }, [section]);

  async function handleSave() {
    setIsSaving(true);
    try {
      const modifiedBy = user?.attributes?.email || user?.username || 'user';
      await updateCardGridSection(pageId, section.position, data, modifiedBy);
      setIsEditing(false);
    } catch (e) {
      console.error('Error saving card grid:', e);
      // eslint-disable-next-line no-alert
      alert('Failed to save section. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() {
    setData(section?.data || defaultCardGridData());
    setIsEditing(false);
  }

  function updateTitle(value) {
    setData((prev) => ({ ...prev, title: value }));
  }

  function updateCard(idx, patch) {
    setData((prev) => {
      const next = { ...prev, cards: [...(prev.cards || [])] };
      next.cards[idx] = { ...next.cards[idx], ...patch };
      return next;
    });
  }

  function addCard() {
    setData((prev) => ({ ...prev, cards: [...(prev.cards || []), defaultCard()] }));
  }

  function removeCard(idx) {
    setData((prev) => {
      const next = { ...prev, cards: [...(prev.cards || [])] };
      next.cards.splice(idx, 1);
      return next;
    });
  }

  function updateCardPartners(idx, partners) {
    setData((prev) => {
      const next = { ...prev, cards: [...(prev.cards || [])] };
      next.cards[idx] = { ...next.cards[idx], partners };
      return next;
    });
  }
  function addCardPartner(idx) {
    const current = normalizePartners(data.cards?.[idx]?.partners);
    updateCardPartners(idx, [...current, { name: '', logoUrl: '' }]);
  }
  function updateCardPartner(idx, pIdx, patch) {
    const current = normalizePartners(data.cards?.[idx]?.partners);
    const next = current.map((p, i) => (i === pIdx ? { ...p, ...patch } : p));
    updateCardPartners(idx, next);
  }
  function removeCardPartner(idx, pIdx) {
    const current = normalizePartners(data.cards?.[idx]?.partners);
    const next = current.filter((_, i) => i !== pIdx);
    updateCardPartners(idx, next);
  }

  if (isEditing) {
    return (
      <div className="card-grid-block card-grid-block--editing">
        <Form.Group className="mb-3">
          <Form.Label>Section title</Form.Label>
          <Form.Control
            type="text"
            value={data.title || ''}
            onChange={(e) => updateTitle(e.target.value)}
          />
        </Form.Group>

        {(data.cards || []).map((card, idx) => (
          <div key={idx} className="card-grid-block__editor-card">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Card {idx + 1}</strong>
              <Button variant="outline-danger" size="sm" onClick={() => removeCard(idx)}>
                <Trash /> Remove
              </Button>
            </div>
            <Row className="g-2">
              <Col md={6}>
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  value={card.title || ''}
                  onChange={(e) => updateCard(idx, { title: e.target.value })}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Link URL</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="/page/tracks/qkd-challenge"
                  value={card.linkUrl || ''}
                  onChange={(e) => updateCard(idx, { linkUrl: e.target.value })}
                />
              </Col>
              <Col md={12}>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={card.description || ''}
                  onChange={(e) => updateCard(idx, { description: e.target.value })}
                />
              </Col>
              <Col md={12}>
                <Form.Label className="mb-1">Partners (optional)</Form.Label>
                <div className="card-grid-block__partners-editor">
                  {normalizePartners(card.partners).map((p, pIdx) => (
                    <div key={pIdx} className="card-grid-block__partner-editor">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">Partner {pIdx + 1}</small>
                        <Button variant="outline-danger" size="sm" onClick={() => removeCardPartner(idx, pIdx)}>
                          <Trash />
                        </Button>
                      </div>
                      <Row className="g-2">
                        <Col md={4}>
                          <Form.Label>Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="City of Calgary"
                            value={p.name || ''}
                            onChange={(e) => updateCardPartner(idx, pIdx, { name: e.target.value })}
                          />
                        </Col>
                        <Col md={8}>
                          <ImageUrlField
                            label="Logo"
                            value={p.logoUrl}
                            onChange={(url) => updateCardPartner(idx, pIdx, { logoUrl: url })}
                          />
                        </Col>
                      </Row>
                    </div>
                  ))}
                  <Button variant="outline-primary" size="sm" onClick={() => addCardPartner(idx)}>
                    <PlusCircle /> Add partner
                  </Button>
                </div>
              </Col>
              <Col md={5}>
                <Form.Label>Status label</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Open for submissions"
                  value={card.status || ''}
                  onChange={(e) => updateCard(idx, { status: e.target.value })}
                />
              </Col>
              <Col md={3}>
                <Form.Label>Status color</Form.Label>
                <Form.Select
                  value={card.statusColor || 'blue'}
                  onChange={(e) => updateCard(idx, { statusColor: e.target.value })}
                >
                  {STATUS_COLORS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label>Icon</Form.Label>
                <Form.Select
                  value={card.icon || 'gear'}
                  onChange={(e) => updateCard(idx, { icon: e.target.value })}
                >
                  {ICON_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </div>
        ))}

        <Button variant="outline-primary" size="sm" onClick={addCard} className="mb-3">
          <PlusCircle /> Add card
        </Button>

        <div className="card-grid-block__editor-actions">
          <Button variant="success" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</>
            ) : (
              <><CheckLg /> Save</>
            )}
          </Button>
          <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
            <XLg /> Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="card-grid-block card-grid-block--view">
      <CardGridView data={data} />
      <RoleGuard role="admin">
        <div className="admin-controls">
          <Button
            variant="outline-secondary"
            size="sm"
            className="edit-btn"
            onClick={() => setIsEditing(true)}
          >
            <PencilFill /> Edit cards
          </Button>
          <SectionControls
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onDelete={onDelete}
            isFirstSection={isFirstSection}
            isLastSection={isLastSection}
          />
        </div>
      </RoleGuard>
    </div>
  );
}

export default CardGridBlock;
